import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { CheckCircle, IndianRupee, Calendar, Clock, Award, Video, Users, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : null;

export default function Training() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleEnroll = async () => {
    if (!API) {
      toast.error('Backend is not configured yet');
      return;
    }
    if (!token) {
      toast.error('कृपया पहले लॉगिन करें');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/payments/create-order`,
        {
          amount: 10998,
          currency: 'INR',
          student_id: user.id,
          course_id: 'digital_success_program_15days'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const options = {
        key: response.data.key_id,
        amount: response.data.amount,
        currency: response.data.currency,
        order_id: response.data.order_id,
        name: 'MKDRP',
        description: '15 Days Digital Success Program',
        handler: async function (paymentResponse) {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
              student_id: user.id
            });
            toast.success('Payment successful! आपका नामांकन सफल रहा');
            navigate('/dashboard');
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.full_name,
          email: user.email
        },
        theme: {
          color: '#FF9933'
        }
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.info('Mock payment mode - Enrollment successful!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment initiation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <WhatsAppButton />

      <section className="bg-gradient-to-br from-orange-500 to-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-extrabold font-heading mb-6"
            data-testid="training-title"
          >
            Training Program
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-2xl font-bold"
          >
            15 Days Digital Success Program
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-10 rounded-3xl shadow-2xl sticky top-24">
                <h2 className="text-3xl font-bold font-heading text-center mb-8 text-gray-900">
                  Course Fee Structure
                </h2>
                
                <div className="space-y-6 mb-8">
                  <div className="bg-white p-6 rounded-xl shadow-md flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1 font-hindi">पंजीकरण शुल्क</p>
                      <p className="text-lg font-semibold text-gray-900">Registration Fee</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center text-2xl font-bold text-orange-600">
                        <IndianRupee className="w-6 h-6" />
                        <span>999</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-md flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1 font-hindi">कोर्स शुल्क</p>
                      <p className="text-lg font-semibold text-gray-900">Course Fee</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center text-2xl font-bold text-orange-600">
                        <IndianRupee className="w-6 h-6" />
                        <span>9999</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-500 to-red-600 p-6 rounded-xl shadow-lg flex justify-between items-center text-white">
                    <div>
                      <p className="text-sm mb-1 font-hindi">कुल कोर्स शुल्क</p>
                      <p className="text-xl font-bold">Total Course Fee</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center text-3xl font-extrabold">
                        <IndianRupee className="w-8 h-8" />
                        <span>10,998</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={handleEnroll}
                  disabled={loading}
                  className="w-full bg-white text-orange-600 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 disabled:opacity-50"
                  data-testid="enroll-payment-button"
                >
                  {loading ? 'Processing...' : 'अभी नामांकन करें - Enroll Now'}
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold font-heading mb-6 text-gray-900">
                  Course Details
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Calendar className="w-6 h-6 text-orange-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Course Duration</p>
                      <p className="text-gray-600">15 Days Training</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Clock className="w-6 h-6 text-orange-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Class Duration</p>
                      <p className="text-gray-600">90 Minutes per session</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Award className="w-6 h-6 text-orange-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Certificate</p>
                      <p className="text-gray-600">On successful completion</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold font-heading mb-6 text-gray-900">
                  What You Will Learn
                </h3>
                <div className="space-y-3">
                  {[
                    "सोशल मीडिया ग्रोथ (Social Media Growth)",
                    "कंटेंट क्रिएशन (Content Creation)",
                    "ऑनलाइन इनकम (Online Income)",
                    "वीडियो एडिटिंग (Video Editing)",
                    "ब्रांडिंग स्किल्स (Branding Skills)",
                    "साइबर सिक्योरिटी (Cyber Security)"
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3" data-testid={`learning-item-${index}`}>
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700 font-hindi">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-400 to-blue-500 text-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold mb-4 font-hindi">विशेष लाभ</h3>
                <ul className="space-y-2 font-hindi">
                  <li>• पर्सनालिटी डेवलपमेंट सेशन</li>
                  <li>• AI टूल्स की जानकारी</li>
                  <li>• प्रैक्टिकल असाइनमेंट्स</li>
                  <li>• लाइफटाइम सपोर्ट</li>
                  <li>• सर्टिफिकेट ऑफ कंप्लीशन</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold font-heading mb-4">
              <span className="gradient-text">Live Classes System</span>
            </h2>
            <p className="text-xl text-gray-600 font-hindi">
              लाइव ऑनलाइन कक्षाएं और रिकॉर्डेड सत्र
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-xl"
            >
              <Video className="w-16 h-16 text-blue-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Live Training Sessions</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Interactive live classes via Zoom</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Real-time doubt clearing sessions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Screen sharing and practical demonstrations</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Q&A with mentors</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-xl"
            >
              <PlayCircle className="w-16 h-16 text-purple-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Recorded Video Library</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span>Access to all recorded sessions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span>Watch anytime, anywhere</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span>Lifetime access to course materials</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span>Download study materials</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-xl">
            <h3 className="text-2xl font-bold font-heading mb-6 text-gray-900 flex items-center gap-2">
              <Calendar className="w-8 h-8 text-orange-600" />
              Upcoming Live Classes Schedule
            </h3>
            <div className="space-y-4">
              {[
                { date: '23 March 2026', time: '10:00 AM - 11:30 AM', topic: 'Introduction to Digital World', batch: 'Batch 1' },
                { date: '24 March 2026', time: '10:00 AM - 11:30 AM', topic: 'Social Media Platforms Overview', batch: 'Batch 1' },
                { date: '25 March 2026', time: '10:00 AM - 11:30 AM', topic: 'Personal Branding Strategies', batch: 'Batch 1' },
                { date: '26 March 2026', time: '10:00 AM - 11:30 AM', topic: 'Business Branding & Marketing', batch: 'Batch 1' }
              ].map((schedule, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-blue-50 rounded-xl hover:shadow-md transition-all"
                  data-testid={`schedule-${index}`}
                >
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1">{schedule.topic}</h4>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {schedule.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {schedule.time}
                      </span>
                      <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-semibold">
                        {schedule.batch}
                      </span>
                    </div>
                  </div>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors">
                    Join Live
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}